import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Button, IconButton } from '@mui/material';
import { GridColDef, GridCellParams } from '@mui/x-data-grid';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
    ConfirmModal,
    IUser,
    Page,
    PermissionKeys,
    Table,
    useHasPermission,
    useQueryParamsContext,
} from '../../../shared';
import { SearchInput } from '../../../shared/components/search/search.component';
import { useDeleteUser, useUserList } from '../../hooks/user.hook';
import { Edit } from '@mui/icons-material';

export const UsersPage: FC = () => {
    const { t } = useTranslation();
    const { hasPermission } = useHasPermission();
    const hasUsersWritePermissions = hasPermission(PermissionKeys.USERS_WRITE);

    const {
        queryParams: { page, pageSize, sort, search },
        debouncedQueryParams,
        setPage,
        setPageSize,
        setSort,
        setSearch,
    } = useQueryParamsContext('users', {
        sort: [{ field: 'lastName, firstName', sort: 'asc' }],
    });

    const { isPending, data: users } = useUserList(
        {
            page: page,
            limit: pageSize,
            sort: sort[0].field
                .split(', ')
                .map((sortBy) => `${sortBy}:${sort[0].sort?.toUpperCase()}`)
                .join(','),
            search: debouncedQueryParams.search,
        },
        { onGetErrorMessage: t('getUsersError') },
    );

    const { mutateAsync: deleteUser } = useDeleteUser();

    const columns: GridColDef[] = [
        {
            field: 'lastName, firstName',
            headerName: t('nameLabel'),
            minWidth: 200,
            flex: 1,
            valueFormatter: (_, row) => `${row.lastName} ${row.firstName}`,
        },
        {
            field: 'status',
            headerName: t('status'),
            minWidth: 50,
            flex: 0.5,
            sortable: false,
            valueFormatter: (value: string) => t(value),
        },
        {
            field: 'actions',
            headerName: t('actions'),
            width: 100,
            align: 'right',
            sortable: false,
            renderCell: ({ row: { id, firstName, lastName } }: GridCellParams<IUser>) => (
                <>
                    <IconButton component={Link} to={`/admin/users/${id}`}>
                        <Edit />
                    </IconButton>
                    <ConfirmModal
                        onConfirm={() =>
                            id &&
                            deleteUser({
                                id,
                                messages: {
                                    onDeleteSuccessMessage: t('deleteUserSuccess'),
                                    onDeleteErrorMessage: t('deleteUserError'),
                                },
                            })
                        }
                        button={
                            <IconButton aria-label="delete">
                                <DeleteOutlineIcon />
                            </IconButton>
                        }
                        title={t('userDeleteWarningTitle')}
                        text={
                            <>
                                {t('userDeleteWarningText')} <br />
                                <b>
                                    {lastName} {firstName}
                                </b>
                            </>
                        }
                    />
                </>
            ),
        },
    ];

    return (
        <Page
            title={t('users')}
            actions={
                hasUsersWritePermissions ? (
                    [
                        <SearchInput value={search} setValue={setSearch} />,
                        <Button
                            component={Link}
                            to="/admin/users/new"
                            color="primary"
                            variant="contained"
                            data-testid="newUser"
                        >
                            {t('newUser')}
                        </Button>,
                    ]
                ) : (
                    <SearchInput value={search} setValue={setSearch} />
                )
            }
        >
            <Table
                rows={users?.items || []}
                columns={columns}
                page={page}
                pageSize={pageSize}
                setPage={setPage}
                setPageSize={setPageSize}
                rowCount={users?.meta.totalItems || 0}
                loading={isPending}
                sortModel={sort}
                onSortModelChange={setSort}
            />
        </Page>
    );
};
